import React, { useState, useEffect } from 'react';
import { useCart } from '../../context/CartContext';
import { useLanguage } from '../../context/LanguageContext';
import '../DetailedView.css';
import QuoteRequestForm from '../QuoteRequestForm';
import { seasonalDates } from '../../data/seasonaldates';
import { formatAandeelDisplay } from '../../utils/aandeelDisplay';

const DigitalDetailView = ({ selectedRow, columns }) => {
  const [bereikValue, setBereikValue] = useState('');
  const [totalPrice, setTotalPrice] = useState(null);
  const [addedToCart, setAddedToCart] = useState(false);
  const [showAdditionalInfo, setShowAdditionalInfo] = useState(false);
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const { addToCart } = useCart();
  const { t } = useLanguage();
  
  // Check if pricing is CPM-based or fixed
  const isCpmBased = selectedRow['Tariefbasis'] === 'CPM';
  
  // Reset bereikValue when selectedRow changes
  useEffect(() => {
    setBereikValue('');
    setTotalPrice(null);
    setAddedToCart(false);
  }, [selectedRow]);
  
  // Calculate total price when bereikValue changes
  const calculateTotalPrice = (value) => {
    // Only calculate if it's CPM-based
    if (!isCpmBased) {
      return;
    }
    
    const bereik = parseFloat(value);
    
    // Remove the '€' character and convert comma to period for decimal separator
    const basisPrijsRaw = selectedRow['Basisprijs'] || '0';
    const basisPrijsClean = basisPrijsRaw.toString()
      .replace(/€/g, '')
      .replace(/\./g, '')  // Remove thousand separators if present
      .replace(/,/g, '.')  // Convert comma decimal separator to period
      .trim();
    const basisprijs = parseFloat(basisPrijsClean);
    
    if (!isNaN(bereik) && !isNaN(basisprijs) && bereik > 0) {
      setTotalPrice(bereik * basisprijs);
    } else {
      setTotalPrice(null);
    }
  };
  
  // Format price with comma as decimal separator and dot as thousands separator
  const formatPrice = (price) => {
    if (price === null || isNaN(price)) return '0,00';
    
    // Format with 2 decimal places and replace decimal point with comma
    let formattedPrice = price.toFixed(2).replace('.', ',');
    
    // Add thousands separator (dot)
    if (price >= 1000) {
      const parts = formattedPrice.split(',');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      formattedPrice = parts.join(',');
    }
    
    return formattedPrice;
  };
  
  // Get clean basisprijs for display, preserving formatting
  const getCleanBasisprijs = () => {
    if (!selectedRow['Basisprijs']) return '0';
    // Just remove the € symbol but keep the formatting (dots and commas)
    return selectedRow['Basisprijs'].toString()
      .replace(/€/g, '')
      .trim();
  };
  
  // Parse a price string (supports € symbol, dot thousand sep, comma decimal) into a number
  const parseDutchCurrency = (currencyStr) => {
    if (currencyStr === null || currencyStr === undefined) return NaN;
    const cleaned = currencyStr
      .toString()
      .replace(/€/g, '')
      .replace(/\./g, '')
      .replace(/,/g, '.')
      .trim();
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? NaN : parsed;
  };
  
  // Get formatted basisprijs with two decimals (comma decimal, dot thousands)
  const getFormattedBasisprijs = () => {
    const raw = selectedRow['Basisprijs'];
    if (!raw) return null;
    const numeric = parseDutchCurrency(raw);
    if (isNaN(numeric)) return null;
    return formatPrice(numeric);
  };
  
  const handleBereikChange = (e) => {
    const value = e.target.value;
    setBereikValue(value);
    calculateTotalPrice(value);
  };
  
  // Format number with dot as thousands separator, no decimals
  const formatWholeNumber = (number) => {
    if (number === null || isNaN(number)) return '0';
    
    // Convert to integer and format as string
    const intValue = Math.round(number);
    let formattedNumber = intValue.toString();
    
    // Add thousands separator (dot)
    if (intValue >= 1000) {
      formattedNumber = formattedNumber.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    }
    
    return formattedNumber;
  };

  // Handle adding item to cart
  const handleAddToCart = () => {
    // For CPM-based pricing, validate bereik value
    if (isCpmBased && (!bereikValue || totalPrice === null)) {
      return;
    }
    
    // Create a unique ID for the cart item
    const itemId = `${selectedRow['Mediumtype'] || 'Digital'}-${selectedRow['Titel']}-${Date.now()}`;
    
    // Create cart item object
    const cartItem = {
      id: itemId,
      title: selectedRow['Titel'] || 'Unknown Digital Item',
      format: selectedRow['Formaat'] || 'Standard Format',
      medium: selectedRow['Mediumtype'] || 'Digital',
      position: selectedRow['Positie'] || '',
      targetingFactor: selectedRow['Tariefbasis'] || '',
      originalData: selectedRow
    };
    
    // Add price information based on pricing model
    if (isCpmBased) {
      // For CPM, use calculated price
      cartItem.price = formatPrice(totalPrice);
      cartItem.basePrice = selectedRow['Basisprijs'] || '0';
      cartItem.bereik = bereikValue;
      cartItem.impressions = parseFloat(bereikValue) * 1000;
    } else {
      // For fixed price, use the base price directly
      cartItem.price = selectedRow['Basisprijs'] || '0';
      cartItem.pricingModel = 'Fixed';
    }
    
    // Add to cart
    addToCart(cartItem);
    
    // Show success message
    setAddedToCart(true);
    
    // Reset success message after 3 seconds
    setTimeout(() => {
      setAddedToCart(false);
    }, 3000);
  };
  
  // Handle adding fixed price item to cart
  const handleAddFixedPriceToCart = () => {
    handleAddToCart();
  };

  // Handle opening the quote request form
  const handleOpenQuoteForm = () => {
    setShowQuoteForm(true);
  };
  
  // Format seasonal dates for the tooltip
  const formatSeasonalDatesInfo = () => {
    return (
      <div className="seasonal-dates-tooltip">
        <h4>Seizoen Toeslagen</h4>
        <ul>
          {seasonalDates.map(period => (
            <li key={period.id}>
              <strong>{period.name}:</strong> {period.days.length} dagen
              <br/>
              <span className="date-range">{period.days[0]} t/m {period.days[period.days.length - 1]}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <div className="detailed-view digital-view">
      <div className="basic-details-section">
        <div className="detail-section compact">
          <strong className="detailed-field-label small">{t('publication')}</strong>
          <div className="detailed-field-value">{selectedRow['Titel'] || '-'}</div>
        </div>
        
        <div className="detail-section compact">
          <strong className="detailed-field-label small">{t('formatAndPosition')}</strong>
          <div className="detailed-field-value">
            {selectedRow['Formaat'] || '-'} {selectedRow['Positie'] ? `/ ${selectedRow['Positie']}` : ''}
          </div>
        </div>
        
        <div className="detail-section compact">
          <strong className="detailed-field-label small">{t('totalPrice', 'Totaal prijs')}</strong>
          <div className="detailed-field-value price">€ {getFormattedBasisprijs() ?? '-'}</div>
          {selectedRow['Opmerkingen'] && (
            <div className="detailed-field-notes">{selectedRow['Opmerkingen']}</div>
          )}
          <div className="seasonal-surcharge-info">
            <span className="info-text">Let op, prijzen zijn exclusief seizoens toeslag</span>
            <div className="icon-surcharge-info" 
                 onMouseEnter={() => setShowTooltip(true)}
                 onMouseLeave={() => setShowTooltip(false)}>
              {showTooltip && (
                <div className="seasonal-tooltip">
                  {formatSeasonalDatesInfo()}
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="detail-section compact">
          <div 
            className="foldable-header" 
            onClick={() => setShowAdditionalInfo(!showAdditionalInfo)}
          >
            <strong className="detailed-field-label small">{t('additionalInformation')}</strong>
            <span className={`fold-icon ${showAdditionalInfo ? 'open' : ''}`}>
              {showAdditionalInfo ? '−' : '+'}
            </span>
          </div>
          
          {showAdditionalInfo && (
            <div className="foldable-content">
              {columns
                .filter(column => 
                  ['Medium', 'Plaatsingsdag', 'Tariefbasis', 'Bereik', 'Positie', 'Aandeel', 'Provincie'].includes(column)
                )
                .map((column, index) => (
                  <div key={index} className="detail-item">
                    <span className="detail-item-label">{column}:</span>
                    <span className="detail-item-value">{column === 'Aandeel' ? formatAandeelDisplay(selectedRow[column]) || '-' : (selectedRow[column] || '-')}</span>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>
      
      {isCpmBased ? (
        // CPM-based pricing calculation section
        <div className="calculation-section">
          <h4 className="calculation-title">{t('calculatePrice')}</h4>
          
          <div className="calculation-description">
            {t('enterCampaignReach')}
          </div>
          
          <div className="detail-section">
            <div className="calculation-step">
              <div className="step-content">
                <strong className="detailed-field-label small">{t('campaignReach')}</strong>
                <div className="calculator-input">
                  <input 
                    id="bereik-input"
                    type="number" 
                    value={bereikValue} 
                    onChange={handleBereikChange}
                    placeholder={t('enterValue')}
                    min="0"
                    className="small-input"
                  />
                  <span className="input-suffix">CPM</span>
                </div>
                
                {bereikValue && !isNaN(parseFloat(bereikValue)) && (
                  <div className="impressions-display">
                    <span className="impressions-value">{formatWholeNumber(parseFloat(bereikValue) * 1000)}</span>
                    <span className="impressions-label">{t('totalImpressions')}</span>
                  </div>
                )}
              </div>
            </div>
            
            {bereikValue && !isNaN(parseFloat(bereikValue)) && (
              <div className="price-result">
                <div className="price-header">{t('totalCampaignCost')}</div>
                <div className="price-content">
                  <div className="price-amount">€ {formatPrice(totalPrice)}</div>
                  <div className="price-details">
                    <div className="price-detail-item">
                      <span className="detail-label">{t('impressions')}:</span>
                      <span className="detail-value">{formatWholeNumber(parseFloat(bereikValue) * 1000)}</span>
                    </div>
                    <div className="price-detail-item">
                      <span className="detail-label">{t('baseRate')}:</span>
                      <span className="detail-value">€ {getCleanBasisprijs()}</span>
                    </div>
                  </div>
                </div>
                
                <div className="cart-actions">
                  <button 
                    className="add-to-cart-btn" 
                    onClick={handleAddToCart}
                    disabled={!bereikValue || isNaN(parseFloat(bereikValue)) || parseFloat(bereikValue) <= 0}
                  >
                    {t('addToCart')}
                  </button>
                  {addedToCart && (
                    <div className="added-to-cart-message">
                      {t('addedToCart')}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        // Fixed price section
        <div className="fixed-price-section">
          <h4 className="fixed-price-title">{t('fixedPriceItem')}</h4>
          
          <div className="fixed-price-description">
            {t('fixedPriceDescription')}
          </div>
          
          <div className="price-display">
            <div className="price-header">{t('itemPrice')}</div>
            <div className="price-amount">€ {getFormattedBasisprijs() ?? '0,00'}</div>
          </div>
          
          <div className="cart-actions">
            <button 
              className="add-to-cart-btn" 
              onClick={handleAddFixedPriceToCart}
            >
              {t('addToCart')}
            </button>
            {addedToCart && (
              <div className="added-to-cart-message">
                {t('addedToCart')}
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Quote Request Form */}
      <QuoteRequestForm 
        isOpen={showQuoteForm}
        onClose={() => setShowQuoteForm(false)}
        selectedProduct={selectedRow}
        bereikValue={bereikValue}
      />
    </div>
  );
};

export default DigitalDetailView; 